import api from '.'

/**
 * AdminOrganizationBillingBalanceApi class handles operations related to the billing balance of an organization's
 * administrative functions. This class provides methods to interact with the backend services for retrieving and
 * managing billing balance information.
 */
class AdminOrganizationBillingBalanceApi {
    /**
     * Creates a new inflow record in the billing system.
     *
     * @param {string} organization_billing_profile_id - The identifier for the organization's billing profile.
     * @param {string} organization_id - The identifier for the organization.
     * @param {number} billing_value - The value of the billing transaction.
     * @param {string} billing_note - A note associated with the billing transaction.
     * @param {string} billing_transaction_date - The date of the billing transaction.
     * @param {string} billing_intended_campaign - The campaign intended for the billing transaction.
     * @param {string} billing_invoice_number - The invoice number for the billing transaction.
     * @param {string} billing_client_name - The name of the billing client.
     * @returns {Promise<object>} The response data from the API call.
     */
    createInflow(
        organization_billing_profile_id,
        organization_id,
        billing_value,
        billing_note,
        billing_transaction_date,
        billing_intended_campaign,
        billing_invoice_number,
        billing_client_name
    ) {
        const payload = {
            organization_billing_profile_id,
            organization_id,
            billing_value,
            billing_note,
            billing_transaction_date,
            billing_intended_campaign,
            billing_invoice_number,
            billing_client_name
        }

        return api
            .sentRequest(
                '/api/organization/admin/billing/balance/create-inflow',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Edits an inflow transaction in the organization's billing profile.
     *
     * @param {string} organization_billing_profile_id - The ID of the organization's billing profile.
     * @param {string} organization_id - The ID of the organization.
     * @param {string} organization_billing_balance_id - The ID of the organization's billing balance.
     * @param {string} billing_note - The note associated with the billing.
     * @param {string} billing_transaction_date - The date of the billing transaction.
     * @param {string} billing_intended_campaign - The campaign intended for the billing inflow.
     * @param {string} billing_invoice_number - The invoice number for the billing inflow.
     * @param {string} billing_client_name - The name of the billing client.
     * @returns {Promise<Object>} A promise that resolves to the response data of the edit request.
     */
    editInflow(
        organization_billing_profile_id,
        organization_id,
        organization_billing_balance_id,
        billing_note,
        billing_transaction_date,
        billing_intended_campaign,
        billing_invoice_number,
        billing_client_name
    ) {
        const payload = {
            organization_billing_profile_id,
            organization_id,
            organization_billing_balance_id,
            billing_note,
            billing_transaction_date,
            billing_intended_campaign,
            billing_invoice_number,
            billing_client_name
        }

        return api
            .sentRequest(
                '/api/organization/admin/billing/balance/edit-inflow',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Cancels an inflow transaction for an organization's billing balance.
     *
     * @param {string} organization_billing_profile_id - The billing profile ID of the organization.
     * @param {string} organization_id - The ID of the organization.
     * @param {string} organization_billing_balance_id - The billing balance ID of the organization.
     * @param {string} billing_note - A note associated with the billing transaction.
     * @param {string} billing_transaction_date - The date of the billing transaction.
     * @param {string} billing_intended_campaign - The intended campaign for the billing transaction.
     * @param {string} billing_invoice_number - The invoice number associated with the billing transaction.
     * @param {string} billing_client_name - The name of the client associated with the billing transaction.
     * @returns {Promise<Object>} The response data from the API call.
     */
    cancelInflow(
        organization_billing_profile_id,
        organization_id,
        organization_billing_balance_id,
        billing_note,
        billing_transaction_date,
        billing_intended_campaign,
        billing_invoice_number,
        billing_client_name
    ) {
        const payload = {
            organization_billing_profile_id,
            organization_id,
            organization_billing_balance_id,
            billing_note,
            billing_transaction_date,
            billing_intended_campaign,
            billing_invoice_number,
            billing_client_name
        }

        return api
            .sentRequest(
                '/api/organization/admin/billing/balance/cancel-inflow',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Creates a cancellation for a given billing profile and balance.
     *
     * @param {string} organization_billing_profile_id - ID of the organization's billing profile.
     * @param {string} organization_billing_balance_id - ID of the organization's billing balance.
     * @param {string} organization_id - ID of the organization.
     * @param {string} billing_note - Note related to the billing.
     * @returns {Promise<object>} Response data from the API.
     */
    createCancellation(
        organization_billing_profile_id,
        organization_billing_balance_id,
        organization_id,
        billing_note
    ) {
        const payload = {
            organization_billing_profile_id,
            organization_billing_balance_id,
            organization_id,
            billing_note
        }

        return api
            .sentRequest(
                '/api/organization/admin/billing/balance/create-cancellation',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Transfers balance between billing profiles within an organization.
     *
     * @param {string} organization_id - The ID of the organization.
     * @param {string} source_organization_billing_profile_id - The ID of the source billing profile.
     * @param {string} target_organization_billing_profile_id - The ID of the target billing profile.
     * @param {number} billing_value - The value of the billing transaction.
     * @param {string} billing_note - A note associated with the billing transaction.
     * @param {string} billing_transaction_date - The date of the billing transaction.
     * @param {string} billing_intended_campaign - The intended campaign for the billing transaction.
     * @param {string} billing_invoice_number - The invoice number for the billing transaction.
     * @param {string} billing_client_name - The client's name associated with the billing transaction.
     * @returns {Promise<Object>} A promise that resolves to the response data of the API call.
     */
    transferBalance(
        organization_id,
        source_organization_billing_profile_id,
        target_organization_billing_profile_id,
        billing_value,
        billing_note,
        billing_transaction_date,
        billing_intended_campaign,
        billing_invoice_number,
        billing_client_name
    ) {
        const payload = {
            organization_id,
            source_organization_billing_profile_id,
            target_organization_billing_profile_id,
            billing_value,
            billing_note,
            billing_transaction_date,
            billing_intended_campaign,
            billing_invoice_number,
            billing_client_name
        }

        return api
            .sentRequest(
                '/api/organization/admin/billing/balance/transfer-balance',
                payload,
                true
            )
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }
}

export const adminOrganizationBillingBalanceApi =
    new AdminOrganizationBillingBalanceApi()
